import React from 'react';
import { products } from '../stripe-config';
import { apiJson } from './api';
import { getVisitorId } from './visitor';

interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

interface DisplayPrice {
  amount: number; // minor units (cents)
  currency: string;
  mode: string;
}

let cachedPrice: DisplayPrice | null | undefined;
let priceFetch: Promise<DisplayPrice | null> | null = null;

/**
 * The real price from the Stripe price object (single source of truth for
 * what a checkout charges). Falls back to VITE_PRICE_LIFETIME when the API
 * is unreachable or Stripe is not configured.
 */
export async function fetchDisplayPrice(): Promise<DisplayPrice | null> {
  if (cachedPrice !== undefined) return cachedPrice;
  if (!priceFetch) {
    priceFetch = apiJson<{ price: DisplayPrice }>('/api/stripe/config')
      .then((data) => {
        cachedPrice = data.price ?? null;
        return cachedPrice;
      })
      .catch(() => {
        cachedPrice = null;
        return null;
      })
      .finally(() => {
        priceFetch = null;
      });
  }
  return priceFetch;
}

/** Formatted display price ("$30", "$29.99"), or null while loading. */
export function useDisplayPrice(): string | null {
  const [display, setDisplay] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    fetchDisplayPrice().then((price) => {
      if (cancelled) return;
      if (price && price.amount > 0) {
        const formatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: price.currency.toUpperCase(),
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(price.amount / 100);
        setDisplay(formatted.replace(/^\$/, ''));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return display;
}

export async function createCheckoutSession({
  priceId,
  successUrl,
  cancelUrl,
  mode = 'payment',
  visitorId,
}: {
  priceId: string;
  successUrl: string;
  cancelUrl: string;
  mode?: 'payment' | 'subscription';
  visitorId: string;
}): Promise<void> {
  const data = await apiJson<CheckoutSessionResponse>('/api/stripe/checkout', {
    method: 'POST',
    body: JSON.stringify({
      price_id: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode,
      visitor_id: visitorId,
    }),
  });

  if (!data.url) {
    throw new Error('No checkout URL received from server');
  }

  window.location.href = data.url;
}

export async function redirectToCheckout(
  productOrPriceId: keyof typeof products | string,
  mode: 'payment' | 'subscription' = 'payment',
): Promise<void> {
  const product =
    productOrPriceId in products
      ? products[productOrPriceId as keyof typeof products]
      : null;

  const priceId = product?.priceId ?? productOrPriceId;
  const checkoutMode = product?.mode ?? mode;
  const visitorId = getVisitorId();

  const successUrl = `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}&visitor_id=${visitorId}`;
  const cancelUrl = `${window.location.origin}/checkout/cancel`;

  await createCheckoutSession({
    priceId,
    successUrl,
    cancelUrl,
    mode: checkoutMode,
    visitorId,
  });
}
